"use client";

import { truncateAddress } from "@aptos-labs/ts-sdk";
import { IconCaretLeft, IconCaretRight } from "@decibeltrade/react-icons";
import { PointsLeaderboardSortKey, PointsLeaderboardTierFilter } from "@decibeltrade/sdk";
import { focusRing } from "@decibeltrade/web-core/recipes";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Clickable } from "@/components/clickable";
import { MediaQuery } from "@/components/media-query";
import { usePointsLeaderboardQuery } from "@/contexts/queries/points-leaderboard";
import { NumberFormatter } from "@/utils/number-formatter";

type TierFilter = PointsLeaderboardTierFilter;

const LEADERBOARD_PAGE_SIZE = 20;

const tierIcons: Record<TierFilter, string> = {
  top20: "/images/points/tier-icon-top20.webp",
  diamond: "/images/points/tier-icon-diamond.webp",
  doublePlatinum: "/images/points/tier-icon-dbl-platinum.webp",
  gold: "/images/points/tier-gold-record.webp",
};

const TIER_IDS: TierFilter[] = ["top20", "diamond", "doublePlatinum", "gold"];

export function PointsLeaderboard() {
  const t = useTranslations("points.leaderboard");
  const [selectedTierIndex, setSelectedTierIndex] = useState(2); // Start at doublePlatinum
  const [sortKey] = useState<PointsLeaderboardSortKey>("total_amps");
  const [page, setPage] = useState(0);

  const selectedTier = TIER_IDS[selectedTierIndex] ?? "doublePlatinum";

  const { data, isLoading, isError } = usePointsLeaderboardQuery({
    limit: LEADERBOARD_PAGE_SIZE,
    offset: page * LEADERBOARD_PAGE_SIZE,
    sortKey,
    sortDir: "DESC",
    tier: selectedTier,
  });

  const tiers: Array<{ id: TierFilter; label: string }> = [
    { id: "top20", label: t("tiers.top20") },
    { id: "diamond", label: t("tiers.diamond") },
    { id: "doublePlatinum", label: t("tiers.doublePlatinum") },
    { id: "gold", label: t("tiers.gold") },
  ];

  const currentTier = tiers[selectedTierIndex];
  const selectedTierLabel = currentTier?.label ?? "";

  const handlePrevTier = () => {
    setSelectedTierIndex((prev) => (prev === 0 ? tiers.length - 1 : prev - 1));
    setPage(0);
  };

  const handleNextTier = () => {
    setSelectedTierIndex((prev) => (prev === tiers.length - 1 ? 0 : prev + 1));
    setPage(0);
  };

  const totalPages = data?.total_count ? Math.ceil(data.total_count / LEADERBOARD_PAGE_SIZE) : 0;
  const items = data?.items ?? [];

  return (
    <div
      className={stack({
        w: "full",
        gap: { base: "16", md: "24" },
      })}
    >
      {/* Mobile Tier Selector - Single tier with arrows */}
      <MediaQuery display="below" breakpoint="md">
        {(className) => (
          <div
            className={cx(
              flex({
                justify: "space-between",
                alignItems: "center",
                py: "8",
                px: "16",
              }),
              className,
            )}
          >
            <Clickable
              className={css({
                p: "4",
                _hover: { opacity: "0.7" },
                _active: { opacity: "0.5" },
              })}
              onClick={handlePrevTier}
            >
              <IconCaretLeft className={css({ w: "24", h: "24", color: "text.primary" })} />
            </Clickable>

            <div
              className={flex({
                gap: "8",
                alignItems: "center",
              })}
            >
              <div
                className={css({
                  w: "32",
                  h: "32",
                  position: "relative",
                  rounded: "full",
                  boxShadow:
                    selectedTier === "top20"
                      ? "[0px 0px 10px 0px #9cb9c5]"
                      : selectedTier === "diamond"
                        ? "[0px 0px 10px 0px rgba(100, 200, 255, 0.5)]"
                        : selectedTier === "doublePlatinum"
                          ? "[0px 0px 10px 0px rgba(200, 200, 200, 0.5)]"
                          : "[0px 0px 10px 0px rgba(255, 208, 0, 0.5)]",
                })}
              >
                <Image
                  src={tierIcons[selectedTier]}
                  alt={selectedTierLabel}
                  width={32}
                  height={32}
                  className={css({ w: "full", h: "full", objectFit: "contain" })}
                />
              </div>
              <span
                className={css({
                  textStyle: "heading.xs",
                  color: "text.tertiary",
                  textTransform: "uppercase",
                  textAlign: "center",
                })}
              >
                {selectedTierLabel}
              </span>
            </div>

            <Clickable
              className={css({
                p: "4",
                _hover: { opacity: "0.7" },
                _active: { opacity: "0.5" },
              })}
              onClick={handleNextTier}
            >
              <IconCaretRight className={css({ w: "24", h: "24", color: "text.primary" })} />
            </Clickable>
          </div>
        )}
      </MediaQuery>

      {/* Desktop Tier Filter Tabs - All tiers visible */}
      <MediaQuery display="above" breakpoint="md">
        {(className) => (
          <div
            className={cx(
              flex({
                gap: "48",
                justify: "center",
                alignItems: "center",
                py: "16",
              }),
              className,
            )}
          >
            <Clickable
              className={css({
                p: "4",
                _hover: { opacity: "0.7" },
              })}
              onClick={handlePrevTier}
            >
              <IconCaretLeft className={css({ w: "24", h: "24", color: "text.primary" })} />
            </Clickable>

            {tiers.map((tier, index) => (
              <button
                key={tier.id}
                onClick={() => {
                  setSelectedTierIndex(index);
                  setPage(0);
                }}
                className={cx(
                  focusRing(),
                  css({
                    display: "flex",
                    alignItems: "center",
                    gap: "8",
                    bg: "transparent",
                    border: "none",
                    cursor: "pointer",
                    px: "8",
                    py: "4",
                    _hover: { opacity: "0.8" },
                    flexShrink: "0",
                  }),
                  selectedTier === tier.id && css({ "& span": { color: "text.primary" } }),
                )}
              >
                <div className={css({ w: "32", h: "32", position: "relative" })}>
                  <Image
                    src={tierIcons[tier.id]}
                    alt={tier.label}
                    width={32}
                    height={32}
                    className={cx(
                      css({ w: "full", h: "full", objectFit: "contain" }),
                      selectedTier !== tier.id && css({ opacity: "0.6" }),
                    )}
                  />
                </div>
                <span
                  className={css({
                    textStyle: "highlight.lg",
                    color: "text.tertiary",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  })}
                >
                  {tier.label}
                </span>
              </button>
            ))}

            <Clickable
              className={css({
                p: "4",
                _hover: { opacity: "0.7" },
              })}
              onClick={handleNextTier}
            >
              <IconCaretRight className={css({ w: "24", h: "24", color: "text.primary" })} />
            </Clickable>
          </div>
        )}
      </MediaQuery>

      {/* Leaderboard Table */}
      <div
        className={css({
          bg: "background.primary",
          border: "[1px solid token(colors.border.primary)]",
          p: { base: "12", md: "24" },
          overflowX: "auto",
        })}
      >
        <table
          className={css({
            w: "full",
            borderCollapse: "collapse",
            minW: { base: "[500px]", md: "auto" },
          })}
        >
          <thead>
            <tr>
              <th
                className={cx(
                  tableHeaderCellStyles,
                  css({ roundedTopLeft: "200", w: { base: "auto", md: "[341px]" } }),
                )}
              >
                {t("columns.rank")}
              </th>
              <th className={tableHeaderCellStyles}>{t("columns.trader")}</th>
              <th className={tableHeaderCellStyles}>{t("columns.points")}</th>
              <th className={cx(tableHeaderCellStyles, css({ roundedTopRight: "200" }))}>
                {t("columns.referralAmps")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4} className={cx(tableBodyCellStyles, css({ textAlign: "center" }))}>
                  <span className={css({ color: "text.tertiary" })}>{t("loading")}</span>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={4} className={cx(tableBodyCellStyles, css({ textAlign: "center" }))}>
                  <span className={css({ color: "text.tertiary" })}>{t("error")}</span>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className={cx(tableBodyCellStyles, css({ textAlign: "center" }))}>
                  <span className={css({ color: "text.tertiary" })}>{t("noData")}</span>
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.owner}>
                  <td className={tableBodyCellStyles}>{row.rank}</td>
                  <td className={tableBodyCellStyles}>{truncateAddress(row.owner)}</td>
                  <td className={tableBodyCellStyles}>{NumberFormatter.compact(row.total_amps)}</td>
                  <td className={tableBodyCellStyles}>
                    {NumberFormatter.compact(row.referral_amps)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className={flex({
              justify: "center",
              alignItems: "center",
              gap: "16",
              pt: "16",
            })}
          >
            <Clickable
              className={css({
                p: "4",
                _hover: { opacity: "0.7" },
                _disabled: { opacity: "0.3", cursor: "default" },
              })}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <IconCaretLeft className={css({ w: "16", h: "16", color: "text.primary" })} />
            </Clickable>
            <span className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
              {page + 1} / {totalPages}
            </span>
            <Clickable
              className={css({
                p: "4",
                _hover: { opacity: "0.7" },
                _disabled: { opacity: "0.3", cursor: "default" },
              })}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <IconCaretRight className={css({ w: "16", h: "16", color: "text.primary" })} />
            </Clickable>
          </div>
        )}
      </div>
    </div>
  );
}

const tableHeaderCellStyles = css({
  bg: "background.secondary",
  px: "12",
  py: "8",
  textStyle: "body.lg",
  color: "text.tertiary",
  textAlign: "left",
  whiteSpace: "nowrap",
});

const tableBodyCellStyles = css({
  px: "12",
  py: "8",
  textStyle: "body.lg",
  color: "text.secondary",
  textAlign: "left",
  whiteSpace: "nowrap",
});
